/** @file clip.setTrack: sets or removes one node's track (frame keys, hold, visibility; 17.2). */
import type { ClipTrack } from '@zilo/pose3d-format/types/clip';
import { checkArray } from '@zilo/pose3d-format/validate/fields/checkArray';
import { checkNumber } from '@zilo/pose3d-format/validate/fields/checkNumber';
import { checkString } from '@zilo/pose3d-format/validate/fields/checkString';
import { NAME_PATTERN } from '@zilo/pose3d-format/validate/fields/namePattern';
import type { CommandDef } from '../apply/commandDef';
import { checkClip } from '../puppetDoc/checkClip';
import { findClip } from '../puppetDoc/findClip';
import { compact } from '../validate/compact';
import { readInput } from '../validate/readInput';
import { unknownNodeWarnings } from './unknownNodeWarnings';

const frameKeys = checkArray(checkNumber({ int: true, min: 0 }));

function readTrack(value: unknown, path: string): ClipTrack {
  const r = readInput(value, path, ['frameKey', 'hold', 'visible']);
  return compact({
    frameKey: r.opt('frameKey', (k) => r.check(k, frameKeys) as number[]), hold: r.opt('hold', (k) => r.int(k, { min: 1 })),
    visible: r.opt('visible', r.bool),
  });
}

/**
 * `clip.setTrack { clip, node, track }` → `{}`: `track: null` removes the node's track (and `tracks` when it empties);
 * an unknown node is stored with `UNKNOWN_NODE_IN_CLIP`. Inverse: the previous track (or `null`).
 */
export const clipSetTrackCommand: CommandDef<'clip.setTrack'> = {
  name: 'clip.setTrack',
  /** Checks the raw input (VALIDATION under the input path). */
  validate: (input, path) => {
    const r = readInput(input, path, ['clip', 'node', 'track']);
    return { clip: r.str('clip'), node: r.check('node', checkString(NAME_PATTERN)) as string, track: r.nullable('track', (k) => readTrack(r.raw(k), r.at(k))) };
  },
  /** Applies the command to the draft and returns its result and inverse. */
  apply: (draft, input) => {
    const { name, clip } = findClip(draft, input.clip);
    const prev = clip.tracks?.[input.node];
    const inverse = [{ name: 'clip.setTrack' as const, input: { clip: name, node: input.node, track: prev === undefined ? null : structuredClone(prev) } }];
    const tracks = Object.fromEntries(Object.entries(clip.tracks ?? {}).filter(([n]) => n !== input.node));
    if (input.track !== null) tracks[input.node] = structuredClone(input.track);
    if (Object.keys(tracks).length === 0) delete clip.tracks;
    else clip.tracks = tracks;
    checkClip(name, clip);
    const warnings = unknownNodeWarnings(draft.puppet, name, clip).filter((w) => w.params?.node === input.node);
    return { result: {}, warnings, inverse };
  },
};
